import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import {
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableContainer,
  Paper,
  Box,
  Typography,
  Skeleton,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const useStyles = makeStyles((theme) => ({
  root: {},
  content: { padding: 0 },
  actions: { justifyContent: 'flex-end' },
  onlineDot: {
    fontSize: 12,
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5),
  },
  clickableRow: {
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

const DeviceTable = ({ devices, restaurantId, loading }) => {
  const classes = useStyles();
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const handleRowClick = (deviceId) => {
    history.push(`/support/${restaurantId}/${deviceId}`);
  };

  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '—';
    if (typeof timestamp === 'object' && '_seconds' in timestamp) {
      return moment(new Date(timestamp._seconds * 1000)).fromNow();
    }
    return moment(timestamp).fromNow();
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={48} sx={{ mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!devices || devices.length === 0) {
    return (
      <Card>
        <CardContent className={classes.emptyState}>
          <Typography variant="body1" color="textSecondary">
            No devices found for this merchant.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className={classes.content}>
        <TableContainer component={Paper} elevation={0}>
          <Table aria-label="Device list">
            <TableHead>
              <TableRow>
                <TableCell>Device ID</TableCell>
                <TableCell>App Version</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((device) => (
                  <TableRow
                    key={device.deviceId}
                    hover
                    className={classes.clickableRow}
                    onClick={() => handleRowClick(device.deviceId)}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(device.deviceId);
                      }
                    }}
                    aria-label={`Device ${device.vendorIdShort || device.deviceId}`}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {device.vendorIdShort || device.deviceId}
                      </Typography>
                    </TableCell>
                    <TableCell>{device.appVersion || '—'}</TableCell>
                    <TableCell>{getRelativeTime(device.lastSeen)}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <FiberManualRecordIcon
                          className={classes.onlineDot}
                          sx={{
                            color: device.isOnline ? 'success.main' : 'grey.400',
                          }}
                        />
                        <Typography variant="body2">
                          {device.isOnline ? 'Online' : 'Offline'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={devices.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      </CardActions>
    </Card>
  );
};

DeviceTable.propTypes = {
  devices: PropTypes.array,
  restaurantId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
};

export default DeviceTable;
